import { intakeSlugProblem, normaliseIntakeSlug } from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { updateRow } from "../../lib/queries";
import { supabase, unwrap } from "../../lib/supabase";

interface Person {
  id: string;
  full_name: string;
  intake_slug: string | null;
  gets_lead_alerts: boolean;
  gets_unclaimed_lead_alerts: boolean;
}

/** What somebody would text. Built off the page it is read on, so it is right
 * on production, on test and on a laptop. */
function linkFor(slug: string): string {
  return `${window.location.origin}/go/${slug}`;
}

/**
 * The links a rep texts a customer, and who hears about what comes back.
 *
 * Jonathon, 2026-09-19: "sam needs a link and i need a link."
 *
 * A link is a person's name in a URL, so this screen is deliberately blunt
 * about what changing one costs: the old link is in somebody's text messages
 * and on whatever was printed, and it stops working the moment the name
 * changes. Nothing here is a rename in the reference-list sense — no document
 * carries the slug — but the same instinct applies, which is why the box asks
 * rather than saving as you type.
 */
export function IntakeLinksTool() {
  const [saving, setSaving] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const people = useAsync<Person[]>(
    async () =>
      unwrap(
        await supabase
          .from("profiles")
          .select(
            "id,full_name,intake_slug,gets_lead_alerts,gets_unclaimed_lead_alerts",
          )
          .eq("active", true)
          .order("sort_order")
          .order("full_name"),
      ) as Person[],
    [],
  );

  async function copy(slug: string) {
    try {
      await navigator.clipboard.writeText(linkFor(slug));
      setCopied(slug);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      setProblem(
        t("This browser would not let us copy. Select the link instead."),
      );
    }
  }

  async function saveSlug(person: Person) {
    const typed = drafts[person.id] ?? "";
    setProblem(null);

    // An emptied box takes the link away, which is a real thing somebody wants
    // when a rep leaves. Said plainly rather than treated as a validation
    // failure.
    if (typed.trim() === "") {
      setSaving(person.id);
      try {
        await updateRow("profiles", person.id, { intake_slug: null });
        await people.reload();
        setDrafts((current) => ({ ...current, [person.id]: "" }));
      } catch (error) {
        setProblem((error as Error).message);
      } finally {
        setSaving(null);
      }
      return;
    }

    const wrong = intakeSlugProblem(typed);
    if (wrong) {
      setProblem(wrong);
      return;
    }

    setSaving(person.id);
    try {
      await updateRow("profiles", person.id, {
        intake_slug: normaliseIntakeSlug(typed),
      });
      await people.reload();
      setDrafts((current) => {
        const next = { ...current };
        delete next[person.id];
        return next;
      });
    } catch (error) {
      // A slug somebody else already holds comes back as a unique violation,
      // which is not a sentence anybody should have to read.
      const message = (error as Error).message;
      setProblem(
        /duplicate|unique/i.test(message)
          ? t("Somebody already has that link name. Pick another.")
          : message,
      );
    } finally {
      setSaving(null);
    }
  }

  async function setFlag(
    person: Person,
    field: "gets_lead_alerts" | "gets_unclaimed_lead_alerts",
    value: boolean,
  ) {
    setProblem(null);
    setSaving(person.id);
    try {
      await updateRow("profiles", person.id, { [field]: value });
      await people.reload();
    } catch (error) {
      setProblem((error as Error).message);
    } finally {
      setSaving(null);
    }
  }

  if (people.loading) return <p className="dim">{t("Loading…")}</p>;
  if (people.error) return <ErrorNote error={people.error} />;

  return (
    <div className="intakelinks">
      {problem ? <ErrorNote error={problem} /> : null}

      <p className="dim">
        {t(
          "Text one of these to a customer and they fill their own details in. The lead lands on the board with that person’s name on it.",
        )}
      </p>

      <div className="intakelink-row">
        <div className="intakelink-who">
          {t("Nobody’s — signs, flyers, the truck")}
        </div>
        <code>{linkFor("")}</code>
        <button type="button" className="btn" onClick={() => void copy("")}>
          {copied === "" ? t("Copied") : t("Copy")}
        </button>
      </div>
      <p className="dim small">
        {t(
          "Leads off that one land unassigned, so somebody has to claim them.",
        )}
      </p>

      <table className="grid">
        <thead>
          <tr>
            <th>{t("Name")}</th>
            <th>{t("Their link")}</th>
            <th />
            <th title={t("Told when a lead arrives through somebody's link")}>
              {t("Lead alerts")}
            </th>
            <th
              title={t(
                "Told when a lead arrives through the link with nobody on it",
              )}
            >
              {t("Unclaimed")}
            </th>
          </tr>
        </thead>
        <tbody>
          {(people.data ?? []).map((person) => {
            const draft = drafts[person.id] ?? person.intake_slug ?? "";
            const changed = draft !== (person.intake_slug ?? "");
            return (
              <tr key={person.id}>
                <td>{person.full_name}</td>
                <td>
                  <span className="intakelink-prefix">
                    {window.location.origin}/go/
                  </span>
                  <input
                    aria-label={t("Link name for {name}", {
                      name: person.full_name,
                    })}
                    value={draft}
                    placeholder="—"
                    onChange={(event) =>
                      setDrafts((current) => ({
                        ...current,
                        [person.id]: event.target.value,
                      }))
                    }
                  />
                </td>
                <td className="intakelink-actions">
                  {changed ? (
                    <button
                      type="button"
                      className="btn"
                      disabled={saving === person.id}
                      onClick={() => void saveSlug(person)}
                    >
                      {t("Save")}
                    </button>
                  ) : person.intake_slug ? (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => void copy(person.intake_slug!)}
                    >
                      {copied === person.intake_slug ? t("Copied") : t("Copy")}
                    </button>
                  ) : null}
                </td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={t("{name} hears about leads", {
                      name: person.full_name,
                    })}
                    checked={person.gets_lead_alerts}
                    disabled={saving === person.id}
                    onChange={(event) =>
                      void setFlag(
                        person,
                        "gets_lead_alerts",
                        event.target.checked,
                      )
                    }
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={t("{name} hears about unclaimed leads", {
                      name: person.full_name,
                    })}
                    checked={person.gets_unclaimed_lead_alerts}
                    disabled={saving === person.id}
                    onChange={(event) =>
                      void setFlag(
                        person,
                        "gets_unclaimed_lead_alerts",
                        event.target.checked,
                      )
                    }
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className="dim small">
        {t(
          "Changing somebody’s link name stops the old one working — it is in the text messages they have already sent.",
        )}
      </p>
    </div>
  );
}
